import { type KeyboardEvent, useRef, useState } from 'react';

import { type IResultAnalyticsData } from '../../../../../interface/state/IElectionState';
import { getAssignmentsByEntity } from '../../../../../lib/agent-assignment-api';
import { getResultAnalyticsByScope } from '../../../../../lib/result-analytics-api';
import { CopyValue } from '../../../../utils/copy';
import { AngleDownSVG, AngleRightSVG } from '../../../../utils/svgs';
import { CoverageChip, StatusBadge } from '../components';
import { childScopeOf, deriveCoverage, isLeafRow, puStatus, type RowCoverage } from '../utils';
import Pager from './Pager';

type NodeState = 'idle' | 'loading' | 'loadingMore' | 'error';

interface ITreeNodeProps {
	item: IResultAnalyticsData;
	coverage?: RowCoverage;
	coverageLoading?: boolean;
	electionId?: number;
	onSelect: (item: IResultAnalyticsData) => void;
}

/** One node in the hierarchy. Non-leaf nodes lazily fetch their own children (and
 * those children's coverage) the first time they're expanded, then recurse. Children
 * beyond the first page are appended on demand via "Load more" so nothing is hidden. */
const TreeNode = ({ item, coverage, coverageLoading, electionId, onSelect }: ITreeNodeProps) => {
	const leaf = isLeafRow(item);
	const [expanded, setExpanded] = useState(false);
	const [children, setChildren] = useState<IResultAnalyticsData[] | null>(null);
	const [childCoverage, setChildCoverage] = useState<Map<string, RowCoverage>>(new Map());
	const [page, setPage] = useState(0);
	const [totalPages, setTotalPages] = useState(1);
	const [state, setState] = useState<NodeState>('idle');
	const reqRef = useRef(0);

	const fetchChildren = (nextPage: number) => {
		const scope = childScopeOf(item);
		if (!scope || !electionId) return;
		const reqId = reqRef.current + 1;
		reqRef.current = reqId;
		setState(nextPage <= 1 ? 'loading' : 'loadingMore');
		getResultAnalyticsByScope({ electionId, resultType: scope.resultType, ...scope.scope, pageNumber: nextPage }).then(
			(outcome) => {
				if (reqId !== reqRef.current) return;
				if (!outcome.reachable || outcome.httpStatus < 200 || outcome.httpStatus >= 300 || !outcome.body) {
					setState('error');
					return;
				}
				const kids = outcome.body.data ?? [];
				setChildren((prev) => (nextPage <= 1 ? kids : [...(prev ?? []), ...kids]));
				setPage(nextPage);
				setTotalPages(outcome.body.totalPages ?? 1);
				setState('idle');
				const codes = kids.map((k) => k.code).filter(Boolean);
				if (codes.length) {
					getAssignmentsByEntity(codes, electionId).then((cov) => {
						if (reqId !== reqRef.current) return;
						if (cov.reachable && cov.httpStatus >= 200 && cov.httpStatus < 300) {
							setChildCoverage((prev) => {
								const map = new Map(prev);
								(cov.body?.data?.items ?? []).forEach((it) => map.set(it.entityCode, deriveCoverage(it)));
								return map;
							});
						}
					});
				}
			}
		);
	};

	const toggle = () => {
		if (leaf) return;
		const next = !expanded;
		setExpanded(next);
		if (next && children === null && state !== 'loading') fetchChildren(1);
	};

	const hasMore = children !== null && page < totalPages;

	return (
		<li className='qa-tree__node'>
			<div className='qa-tree__row'>
				{leaf ? (
					<span
						className='qa-tree__caret qa-tree__caret--leaf'
						aria-hidden='true'
					/>
				) : (
					<button
						type='button'
						className='qa-tree__caret'
						aria-expanded={expanded}
						aria-label={expanded ? `Collapse ${item.code}` : `Expand ${item.code}`}
						onClick={toggle}
					>
						{expanded ? <AngleDownSVG /> : <AngleRightSVG />}
					</button>
				)}
				<button
					type='button'
					className={`qa-tree__label qa-tree__label--${puStatus(item)}`}
					onClick={() => onSelect(item)}
					onKeyDown={(e: KeyboardEvent) => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault();
							onSelect(item);
						}
					}}
				>
					<span className='qa-tree__code'>{item.code}</span>
					<CoverageChip
						coverage={coverage}
						loading={coverageLoading}
					/>
					<StatusBadge status={puStatus(item)} />
				</button>
				<CopyValue
					value={item.code}
					label='code'
					className='qa-tree__copy'
				/>
			</div>

			{expanded && !leaf ? (
				<ul className='qa-tree__children'>
					{state === 'loading' ? <li className='qa-tree__hint'>Loading…</li> : null}
					{state === 'error' ? (
						<li className='qa-tree__hint'>
							Couldn’t load.{' '}
							<button
								type='button'
								className='qa-tree__retry'
								onClick={() => fetchChildren(page + 1 || 1)}
							>
								Try again
							</button>
						</li>
					) : null}
					{children?.length === 0 && state === 'idle' ? <li className='qa-tree__hint'>Nothing here yet.</li> : null}
					{children?.map((kid) => (
						<TreeNode
							key={kid.id}
							item={kid}
							coverage={childCoverage.get(kid.code)}
							electionId={electionId}
							onSelect={onSelect}
						/>
					))}
					{hasMore ? (
						<li className='qa-tree__hint'>
							<button
								type='button'
								className='qa-tree__retry'
								disabled={state === 'loadingMore'}
								onClick={() => fetchChildren(page + 1)}
							>
								{state === 'loadingMore' ? 'Loading…' : 'Load more'}
							</button>
						</li>
					) : null}
				</ul>
			) : null}
		</li>
	);
};

interface ITreeViewProps {
	rows: IResultAnalyticsData[];
	coverage: Map<string, RowCoverage>;
	coverageLoading: boolean;
	onSelectChild: (item: IResultAnalyticsData) => void;
	electionId?: number;
	entityLabel: string;
	rootLabel?: string;
	rootLevelLabel?: string;
	hasActiveFilter: boolean;
	currentPage: number;
	totalPages: number;
	onPage: (page: number) => void;
}

/** Document-style hierarchy: the agent's own area at the root, expanding to children
 * then grandchildren (lazy-loaded). Each node opens its coverage drawer on click. The
 * top level pages like the other views; deeper levels append via "Load more". */
const TreeView = ({
	rows,
	coverage,
	coverageLoading,
	onSelectChild,
	electionId,
	entityLabel,
	rootLabel,
	rootLevelLabel,
	hasActiveFilter,
	currentPage,
	totalPages,
	onPage,
}: ITreeViewProps) => (
	<div className='qa-tree'>
		{rootLabel ? (
			<div className='qa-tree__root'>
				<span
					className='qa-tree__rooticon'
					aria-hidden='true'
				/>
				<span className='qa-tree__rootlabel'>{rootLabel}</span>
				{rootLevelLabel ? <span className='qa-tree__rootlevel'>{rootLevelLabel}</span> : null}
			</div>
		) : null}
		{rows.length ? (
			<>
				<ul className='qa-tree__children qa-tree__children--top'>
					{rows.map((item) => (
						<TreeNode
							key={item.id}
							item={item}
							coverage={coverage.get(item.code)}
							coverageLoading={coverageLoading}
							electionId={electionId}
							onSelect={onSelectChild}
						/>
					))}
				</ul>
				<Pager
					currentPage={currentPage}
					totalPages={totalPages}
					onPage={onPage}
					showPageNote={false}
				/>
			</>
		) : (
			<div className='qa-empty-filter'>
				<p>{hasActiveFilter ? 'No results match your filters.' : `No ${entityLabel.toLowerCase()} to show.`}</p>
			</div>
		)}
	</div>
);

export default TreeView;
